import React, { useEffect, useState } from 'react';
import { Switch, Button, Upload, Form, Input, Select } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { PlusOutlined, DeleteFilled, EyeFilled } from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import { SortableTableHeader, DataTable } from '../../../components/common';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getTeam,
  removeTeam,
  addTeam,
  blockTeam,
  getSport,
  getSeason,
} from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule, getBase64 } from '../../../utils';
import { Link } from 'react-router-dom';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';

const { Option } = Select;

// TypeScript interfaces
interface SportData {
  _id: string;
  sportname: string;
}

interface TeamData {
  _id: string;
  teamname: string;
  teamLogo: string;
  isActive: boolean;
  sport?: {
    _id: string;
    sportname: string;
  };
}

interface TeamFormValues {
  teamname: string;
  description?: string;
}

const Team: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<TeamFormValues>();

  // Redux state
  const teamData = useAppSelector((state) => state.admin.teamData) as unknown as TeamData[];
  const totalCount = useAppSelector((state) => state.admin.teamCount);
  const sportData = useAppSelector((state) => state.admin.sportData) as unknown as SportData[];

  // Use custom hooks
  const { currentPage, sortOrder, changePage, sortData, setCurrentPage } = useTableState({
    fetchAction: getTeam,
  });

  const { visible, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeTeam,
    idParamName: 'teamId',
  });

  const [loading] = useDataLoading(teamData);

  // Local state for form-specific data
  const [imageUrl, setImageUrl] = useState<string>('');
  const [fileObj, setFileObj] = useState<File | string>('');
  const [sportId, setSportId] = useState<string>('');

  // Load additional data on mount
  useEffect(() => {
    const obj1 = {
      count: 1000,
      page: 0,
    };
    dispatch(getSport(obj1));
    dispatch(getSeason({ page: 0 }));
  }, [dispatch]);

  const handleShowModal = (id: string, name: string, description: string) => {
    showModal(id);
    form.setFieldsValue({ teamname: name, description: description });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      hideModal();
      setCurrentPage(0);

      const formData = new FormData();
      formData.append('teamname', values.teamname);
      formData.append('file', fileObj as File);
      formData.append('sportId', sportId);

      dispatch(addTeam(formData));
    } catch (error) {
      // Validation failed - form will show errors
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done' && info.file.originFileObj) {
      getBase64(info.file.originFileObj, (url) => setImageUrl(url));
      setFileObj(info.file.originFileObj);
    }
  };

  const upload = (file: File) => {
    getBase64(file, (url) => {
      setImageUrl(url);
      setFileObj(file);
    });
  };

  const handleBlockTeam = (teamIdToBlock: string) => {
    const values = {
      teamId: teamIdToBlock,
    };
    dispatch(blockTeam(values));
  };

  const handleMenuClick = (value: string) => {
    setSportId(value);
  };

  const getFilterTeam = (value: string) => {
    setSportId(value);
    dispatch(getTeam({ page: currentPage, sortValue: '', sortOrder: '', sport: value }));
  };

  const sportOptions = sportData.map((x: SportData, index: number) => (
    <Option value={x._id} key={index}>
      {x.sportname}
    </Option>
  ));

  const profilePic = imageUrl ? imageUrl : '';

  // Define table columns
  const columns = [
    {
      key: 'teamname',
      title: (
        <SortableTableHeader
          label="Team Name"
          sortKey="teamname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: TeamData) => item.teamname,
    },
    {
      key: 'sport',
      title: (
        <SortableTableHeader
          label="Sport"
          sortKey="sport.sportname"
          currentSortOrder={sortOrder}
          onSort={sortData}
        />
      ),
      render: (item: TeamData) => (item.sport ? item.sport.sportname : ''),
    },
    {
      key: 'logo',
      title: 'Logo',
      render: (item: TeamData) => (
        <img
          src={item.teamLogo}
          alt="team logo"
          style={{ height: '60px', width: '60px' }}
        />
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (item: TeamData) => (
        <Switch checked={item.isActive} onChange={() => handleBlockTeam(item._id)} />
      ),
    },
    {
      key: 'actions',
      title: 'Action',
      className: 'action-icons',
      render: (item: TeamData) => (
        <>
          <Link to={'/teamdetail/' + item._id}>
            <Button type="link" className="btn-sm mr-2">
              <EyeFilled style={{ color: '#666' }} />
            </Button>
          </Link>
          <Button
            type="link"
            className="btn-sm mr-2"
            onClick={() => showDeleteConfirm(item._id)}
          >
            <DeleteFilled style={{ color: '#666' }} />
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Team"
          subTitle=""
          extra={[
            <Select
              key="2"
              placeholder="Sport"
              style={{ width: 150 }}
              className="mobile-bottom-space"
              onChange={getFilterTeam}
            >
              <Option value="">All</Option>
              {sportOptions}
            </Select>,
            <Button
              key="1"
              type="default"
              onClick={() => handleShowModal('', '', '')}
            >
              <PlusOutlined className="mr-1" /> Add Team
            </Button>,
          ]}
        />
        <DataTable
          columns={columns}
          data={teamData}
          loading={loading}
          currentPage={currentPage}
          pageSize={10}
          total={totalCount}
          onPageChange={changePage}
          sortOrder={sortOrder}
          onSort={sortData}
          rowKey="_id"
        />
        <CustomModal
          handleCancel={hideModal}
          handleOk={handleOk}
          visible={visible}
          title="Team"
          backBtnText="Back"
          submitBtnText="Submit"
        >
          <Form form={form} className="team-form l_hgt0" layout="vertical">
            <Upload
              name="file"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={false}
              customRequest={({ file }) => upload(file as File)}
              onChange={handleChange}
            >
              <img
                src={profilePic ? profilePic : 'http://placehold.it/380x500'}
                alt="team"
                className="img-rounded img-responsive img-104"
              />
            </Upload>
            <Form.Item
              label="Team Name"
              name="teamname"
              rules={[requiredRule('team name')]}
            >
              <Input placeholder="Team" />
            </Form.Item>
            <br />
            <label>Sports :</label>
            <br />
            <Select defaultValue="sport" onChange={handleMenuClick}>
              {sportOptions}
            </Select>
          </Form>
        </CustomModal>
      </div>
    </>
  );
};

export default Team;
